/**
 * Shared timezone constant and helpers for consistent datetime handling.
 *
 * All user-facing dates/times in the Conductor frontend use Asia/Shanghai
 * (CST, UTC+08:00). This module provides the single source of truth for
 * the display timezone and a helper to generate +08:00 ISO timestamps
 * for optimistic UI updates.
 */

/** IANA timezone used for all user-facing date/time formatting. */
export const DISPLAY_TIMEZONE = 'Asia/Shanghai';

/**
 * Points (Credits) per USD — mirror of backend `Settings.points_per_usd`
 * (app/config.py). Org users see 点数 (points) instead of raw USD; 1 point
 * = $0.01 so points = round(cost_usd * POINTS_PER_USD). Keep in sync with
 * the backend constant — both are the single source of truth for their layer.
 */
export const POINTS_PER_USD = 100;

/**
 * Convert a USD cost to whole points (Credits) with thousands separators.
 * Returns "—" for null/undefined/empty/NaN so callers can render it inline.
 */
export function formatPoints(
  usd: string | number | null | undefined,
  perUsd: number = POINTS_PER_USD,
): string {
  if (usd === null || usd === undefined || usd === '') return '—';
  const n = Number(usd);
  if (Number.isNaN(n)) return '—';
  return Math.round(n * perUsd).toLocaleString('en-US');
}

/**
 * Generate an ISO 8601 timestamp with a +08:00 offset for optimistic UI
 * updates (e.g. new message created_at, updated_at).
 *
 * This avoids the "Z" (UTC) suffix that toISOString() produces, which
 * can look wrong when displayed in the CST timezone before the backend
 * round-trips the real timestamp.
 */
export function nowCSTISO(): string {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  const cst = new Date(now.getTime() + offsetMs + 8 * 3600000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${cst.getFullYear()}-${pad(cst.getMonth() + 1)}-${pad(cst.getDate())}` +
    `T${pad(cst.getHours())}:${pad(cst.getMinutes())}:${pad(cst.getSeconds())}+08:00`
  );
}
